import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { selectTxnId, useMapStore } from "stores/map";
import { ChartFocusFeature } from "types";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import { removeCount } from "utils/removeCountFromTitle";

/** The legend key for an item — the same resolution both hooks below use. */
const legendKey = (item: { groupTitle?: string; title?: string }): string =>
  String(item.groupTitle || item.title || "");

/**
 * True when the user chose a colour palette for THIS chart, so the map legend's
 * colours must stand aside and let that palette show. See colorPaletteIsExplicit
 * on ChartFocusFeature for why the palette alone cannot answer this.
 *
 * A chart saved before that flag existed carries no answer, so fall back to
 * comparing the two palette ids: imperfect (it misses a pick the user then
 * reversed) but it never reads a genuine pick as "nothing picked".
 */
export function hasPickedChartFocusPalette(
  chartFocus: Maybe<Partial<ChartFocusFeature>>,
  globalPaletteId?: string
): boolean {
  if (!(chartFocus?.active ?? false)) return false;
  const focusPalette = chartFocus?.properties?.colorPalette;
  return (
    chartFocus?.properties?.colorPaletteIsExplicit ??
    (focusPalette != null && focusPalette.id !== globalPaletteId)
  );
}

/**
 * The focus legend's bin titles, in the order the widget's legend shows them.
 *
 * Reads the SAME source useChartLegendItems falls back to for a wells chart on
 * the global focus (`txnId.legend.legendItems`), so anything ordered by this
 * agrees with the legend the user is looking at rather than with the
 * aggregation's count ranking. See orderSeriesByLegend, which consumes it.
 *
 * Returns an empty array whenever the legend is unavailable — which makes every
 * consumer a no-op rather than an arbitrary reordering.
 */
export function useWellStatusLegendOrder(): string[] {
  const txnId = useMapStore(selectTxnId);
  const legendItems = txnId?.legend?.legendItems;

  return useMemo(() => (legendItems ?? []).map(legendKey).filter(Boolean), [legendItems]);
}

/**
 * The focus legend's bin title → colour, for the request's `seriesColors`.
 *
 * Without it the server spreads the palette over the bins IT returns, ranked by
 * count, while the legend spreads the same palette over the FOCUS's bin order —
 * so whenever the two orders differ the bars are painted the wrong colours. An
 * explicit map removes the dependency on the two orders agreeing.
 *
 * Empty whenever the legend is unavailable (it arrives with the map response,
 * which can land after the first chart request), and the caller omits the field
 * in that case so the server falls back to spreading the palette as before.
 */
export function useWellStatusLegendColors(): Record<string, string> {
  const txnId = useMapStore(selectTxnId);
  const legendItems = txnId?.legend?.legendItems;

  return useMemo(() => {
    const colors: Record<string, string> = {};
    for (const item of legendItems ?? []) {
      const key = legendKey(item);
      if (key && item.color) colors[key] = item.color;
    }
    return colors;
  }, [legendItems]);
}

/**
 * The same bin → colour map, keyed for CLIENT-SIDE painting.
 *
 * The request-shaped map above is sent to the server, which stamps each series'
 * colour. That round-trip is why re-ordering the focus bins used to flicker: the
 * new order applies to the chart the instant the legend changes (see
 * useWellStatusLegendOrder), but the colours could not follow until a fresh
 * aggregation came back — several seconds on a large filter — so the chart sat in
 * a state with the new order and the previous colours. Painting from this map
 * makes the two move together.
 *
 * Keyed on the count-stripped, case-folded title because BOTH sides carry a
 * "(123)" well-count affix and the two counts are of different populations: the
 * legend counts every well in the bin, the aggregation counts the ones it could
 * resolve and classify. Matching on the raw title misses precisely when the two
 * disagree — the same trap fixed server-side in SeriesColorResolver.
 *
 * Empty once the user picks a palette for this chart, which hands the colouring
 * back to the server: painting from the map legend on top of the picked palette is
 * what made the pick look dead. An empty map is safe everywhere — every consumer
 * falls back to the server's own `series.style.hexColor`.
 */
export function useWellStatusSeriesColorMap(
  chartFocus?: Maybe<Partial<ChartFocusFeature>>
): ReadonlyMap<string, string> {
  const txnId = useMapStore(selectTxnId);
  const legendItems = txnId?.legend?.legendItems;
  const globalPalette = useSelector(
    (state: RootState) => state.userSetting.activeColorPalette
  );
  const enabled = !hasPickedChartFocusPalette(chartFocus, globalPalette?.id);

  return useMemo(() => {
    const colors = new Map<string, string>();
    if (!enabled) return colors;
    for (const item of legendItems ?? []) {
      const key = normalizeMatchKey(removeCount(legendKey(item)));
      if (key && item.color) colors.set(key, item.color);
    }
    return colors;
  }, [legendItems, enabled]);
}
